import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: '',
  email: '',
  phone: '',
  address: '',
  propertyType: '',
  service: '',
  customerType: 'new',
  message: '',
  agreeTerms: false
};

const QuoteForm = ({ formType = 'hero', submitLabel = 'Submit Request' }) => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      toast.error('Please agree to the Terms of Service');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...formData, formType });
      toast.success("Thank you! We'll contact you shortly with your free quote.");
      setFormData(initialState);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${formType}-quote-form`}>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Name *</label>
        <Input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Your full name"
          required
          className="border-slate-300"
          data-testid={`${formType}-input-name`}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Email Address *</label>
        <Input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="your@email.com"
          required
          className="border-slate-300"
          data-testid={`${formType}-input-email`}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Phone Number *</label>
        <Input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="(825) 583-7478"
          required
          className="border-slate-300"
          data-testid={`${formType}-input-phone`}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Address *</label>
        <Input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          placeholder="123 Main Street, Calgary"
          required
          className="border-slate-300"
          data-testid={`${formType}-input-address`}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Property Type *</label>
        <select
          name="propertyType"
          value={formData.propertyType}
          onChange={handleInputChange}
          required
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
          data-testid={`${formType}-select-property`}
        >
          <option value="">Select property type</option>
          <option value="home">Home</option>
          <option value="business">Business</option>
        </select>
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Service Type *</label>
        <select
          name="service"
          value={formData.service}
          onChange={handleInputChange}
          required
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
          data-testid={`${formType}-select-service`}
        >
          <option value="">Select a service</option>
          <option value="carpet-cleaning">Carpet Cleaning</option>
          <option value="upholstery-cleaning">Upholstery Cleaning</option>
          <option value="mattress-cleaning">Mattress Cleaning</option>
          <option value="area-rug-cleaning">Area Rug Cleaning</option>
          <option value="commercial">Commercial</option>
        </select>
      </div>
      <div className="flex gap-6">
        <div className="flex items-center space-x-2">
          <input
            type="radio"
            name="customerType"
            id={`${formType}-new-customer`}
            value="new"
            checked={formData.customerType === 'new'}
            onChange={handleInputChange}
            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
          />
          <label htmlFor={`${formType}-new-customer`} className="font-normal cursor-pointer text-sm">New Customer</label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="radio"
            name="customerType"
            id={`${formType}-existing-customer`}
            value="existing"
            checked={formData.customerType === 'existing'}
            onChange={handleInputChange}
            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
          />
          <label htmlFor={`${formType}-existing-customer`} className="font-normal cursor-pointer text-sm">Existing Customer</label>
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-slate-900 mb-1.5 block">Message</label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          placeholder="Tell us about your cleaning needs, number of rooms, preferred date/time, pet stains, etc..."
          rows={3}
          className="border-slate-300"
          data-testid={`${formType}-input-message`}
        />
      </div>
      <div className="flex items-start space-x-2">
        <input
          type="checkbox"
          id={`${formType}-terms`}
          name="agreeTerms"
          checked={formData.agreeTerms}
          onChange={handleInputChange}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          data-testid={`${formType}-checkbox-terms`}
        />
        <label htmlFor={`${formType}-terms`} className="text-sm text-slate-700 leading-tight cursor-pointer">
          I Agree to the <Link to="/terms" target="_blank" className="text-cyan-600 hover:text-cyan-700 underline">Terms of Service</Link>
        </label>
      </div>
      <Button
        type="submit"
        className="w-full bg-cyan-600 hover:bg-cyan-700 py-5 text-base font-semibold"
        disabled={isSubmitting}
        data-testid={`${formType}-submit-btn`}
      >
        {isSubmitting ? 'Submitting...' : submitLabel}
      </Button>
    </form>
  );
};

export default QuoteForm;
